<script setup lang="ts">
import {EditorView, basicSetup} from "codemirror";
import { onMounted, useTemplateRef, watch } from "vue";
import { Compartment } from "@codemirror/state"
import { linter, type Diagnostic } from "@codemirror/lint"
import { useDarkMode } from "shared/themes/composables/darkMode";
import {dracula} from 'thememirror';
import type { FormulaLanguage } from "shared/mapStyles/domain/formulaLanguage";

const model = defineModel<string>();
const props = defineProps<{
  lang: FormulaLanguage
}>();

const { formulaParser, language } = props.lang;

const editorElement = useTemplateRef("editor");
const darkMode = useDarkMode();

onMounted(() => {

  if (editorElement.value === null) throw new Error();

  const regexpLinter = linter(view => {
    const diagnostics: Diagnostic[] = [];

    const text = view.state.doc.toString();

    const result = formulaParser.compile(text);

    if (!result.success) {
        for (const error of result.errors) {
            diagnostics.push({
                from: error.token.from,
                to: error.token.to,
                message: error.error,
                severity: 'error'
            });
        }
    }

    return diagnostics;
});

const languageCompartment = new Compartment();
const themeCompartment = new Compartment();

const editorView = new EditorView({
    parent: editorElement.value,
    doc: model.value,
    extensions: [
      basicSetup,
      EditorView.updateListener.of((update) => {
        if (!update.docChanged) return;
        model.value = update.state.doc.toString();
      }),
      languageCompartment.of(language),
      regexpLinter,
      themeCompartment.of([])
    ]
  });


  watch(darkMode, () => {
    if (darkMode.enabled) {
      editorView.dispatch({
        effects: themeCompartment.reconfigure(dracula)
      });
    } else {
      editorView.dispatch({
        effects: themeCompartment.reconfigure([])
      });
    }
  }, { immediate: true });
});

</script>

<template>
  <div ref="editor" class="editor"></div>
</template>

<style>

.cm-editor {
  margin-bottom: 1px;
  margin-left: 1px;
  margin-right: 1px;
}
</style>
